import { useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import AppTour, { isTourComplete, setTourComplete } from './AppTour';
import { supabase } from '../lib/supabase';
import { useUserContext } from '../lib/UserContextProvider';
import MobileNav from './MobileNav';
import { RoomProvider } from '../contexts/RoomContext';

interface AppLayoutProps {
  children: ReactNode;
}

// v3.1: Sidebar retired. Mobile-first bottom tab bar on every viewport.
// Main content is a 420px centered column on canvas (see per-page components).

function AppLayoutInner({ children }: AppLayoutProps) {
  const { refresh } = useUserContext();
  const location = useLocation();
  const [_tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (location.pathname !== '/app') return;
    if (isTourComplete()) return;
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth.user;
      if (!user || cancelled) {
        if (!user && !cancelled && !isTourComplete()) {
          setTourOpen(true);
        }
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('tour_completed')
        .eq('id', user.id)
        .maybeSingle();
      const done = (profile as { tour_completed?: boolean } | null)?.tour_completed;
      if (!cancelled && !done && !isTourComplete()) {
        setTourOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    // Non-fatal: persist tour completion when the tour signals done.
    void setTourComplete;
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-canvas)', color: 'var(--color-ink-1)' }}>
      <AppTour />
      <main>{children}</main>
      <MobileNav />
    </div>
  );
}

// Wrap the export with RoomProvider so child pages can set their room
const AppLayoutWrapped = ({ children }: AppLayoutProps) => (
  <RoomProvider>
    <AppLayoutInner>{children}</AppLayoutInner>
  </RoomProvider>
);

export default AppLayoutWrapped;
